import classNames from 'classnames';
import { useRouter } from 'next/router';
import { addLocaleToHref } from 'lib/utils/helpers';
import SiteLink from 'components/shared/sitelink';
import { FAQ_HUB_SLUG } from 'lib/contentful/fragments/faq';
import styles from './faqSidebar.module.scss';

// `nestChildUrls`: true → /{prefix}/{parent}/{child} (CS), false → /{prefix}/{child}
// (product, flat). Active state matches strictly by slug so the nested child is
// marked active, not its parent — the visual indent already shows the relationship.
export const buildSidebarItems = (categories, activeCategorySlug, locale, options = {}) => {
  const { urlPrefix = FAQ_HUB_SLUG, nestChildUrls = false } = options;
  return (categories || []).map(category => ({
    id: category.id,
    label: category.shortLabel || category.title,
    href: `/${urlPrefix}/${category.slug}`,
    isActive: category.slug === activeCategorySlug,
    locale,
    children: (category.children || []).map(child => ({
      id: child.id,
      label: child.shortLabel || child.title,
      href: nestChildUrls
        ? `/${urlPrefix}/${category.slug}/${child.slug}`
        : `/${urlPrefix}/${child.slug}`,
      isActive: child.slug === activeCategorySlug,
      locale,
    })),
  }));
};

const SidebarLink = ({ item, locale, router, className }) => (
  <SiteLink
    href={addLocaleToHref(item.href, locale, router)}
    locale={locale}
    className={className}
    aria-current={item.isActive ? 'page' : undefined}
  >
    {item.label}
  </SiteLink>
);

const FaqSidebar = ({ items, locale, backLink, className }) => {
  const router = useRouter();

  if (!items?.length) return null;

  return (
    <nav
      className={classNames(styles.sidebar, className)}
      aria-label={backLink?.label || 'FAQ navigation'}
    >
      {backLink?.href ? (
        <SiteLink
          href={addLocaleToHref(backLink.href, locale, router)}
          locale={locale}
          className={styles.backLink}
        >
          <span aria-hidden="true">‹ </span>
          {backLink.label}
        </SiteLink>
      ) : (
        <span
          className={classNames(styles.backLink, styles.backLinkPlaceholder)}
          aria-hidden="true"
        >
          &nbsp;
        </span>
      )}
      <ul className={styles.list}>
        {items.map(item => (
          <li key={item.id} className={classNames(styles.item, { [styles.active]: item.isActive })}>
            <SidebarLink item={item} locale={locale} router={router} className={styles.link} />
            {item.children?.length > 0 && (
              <ul className={styles.children}>
                {item.children.map(child => (
                  <li
                    key={child.id}
                    className={classNames(styles.childItem, {
                      [styles.active]: child.isActive,
                    })}
                  >
                    <SidebarLink
                      item={child}
                      locale={locale}
                      router={router}
                      className={classNames(styles.link, styles.childLink)}
                    />
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default FaqSidebar;
